import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Trophy } from 'lucide-react';

/**
 * Celebração da meta batida no painel de Ranking.
 *
 * O painel fica horas ligado numa TV da operação. O momento que vale é a ATUALIZAÇÃO que traz o
 * número cruzando a meta — a venda acontece e a tela comemora sozinha, sem ninguém clicar. Quem
 * conta a história depois é o selo permanente no avatar e no card; este efeito some sem resíduo.
 *
 * **A celebração é ENCENADA, não uma rajada só.** A primeira versão despejava todo o papel de uma
 * vez e acabava em seis segundos; quem estava de costas perdia. Agora há um roteiro: dois canhões
 * disparam dos cantos de baixo, a chuva entra do topo em ondas e o anúncio segura o centro — doze
 * segundos na meta, dezoito na supermeta, tempo de alguém atravessar a sala para olhar.
 *
 * Papel, e não brilho: tiras finas girando enquanto caem, nas cores que o painel já usa — o ouro do
 * pódio, o `--accent` do tema e branco. Canvas 2D próprio em vez de biblioteca: são ~120 linhas de
 * física, e uma dependência para um efeito desse tamanho não se paga.
 */

const GRAVIDADE = 900; // px/s²
/**
 * Teto de tiras em cena ao mesmo tempo.
 *
 * O roteiro da supermeta solta 1.310 ao longo de 18 s, mas elas saem de cena enquanto caem. O teto
 * existe para o caso em que não saem — aba restaurada, quadro perdido, painel muito alto — e corta
 * as mais antigas, que já estão perto do fim da queda.
 */
const TETO_DE_PARTICULAS = 700;
const ARRASTO = 0.86;
const DURACAO_SUPERMETA_MS = 18000;
const DURACAO_TOTAL_MS = 12000;
const DURACAO_VENDEDOR_MS = 6000;

/**
 * Duração do anúncio na tela. A chuva de papel termina antes; o texto é que precisa ser lido.
 *
 * A supermeta fica mais tempo e com mais papel: é o alvo esticado, acontece no máximo uma vez por
 * competência, e uma celebração igual à da meta não diria que algo maior aconteceu.
 */
export const duracaoDaCelebracao = (tipo) => {
  if (tipo === 'supermeta') return DURACAO_SUPERMETA_MS;
  return tipo === 'total' ? DURACAO_TOTAL_MS : DURACAO_VENDEDOR_MS;
};

/**
 * O roteiro da celebração: que onda de papel entra, de onde e quando.
 *
 * Ondas em vez de uma rajada é o que faz a cena durar sem ficar parada — papel novo entra enquanto
 * o primeiro ainda cai. Os canhões abrem porque sobem contra a gravidade e por isso são o movimento
 * mais visível; a chuva do topo sustenta depois. Nenhuma onda começa perto do fim: papel entrando
 * com o anúncio já saindo deixaria confete órfão na tela.
 *
 * @returns {Array<{aos:number, quantidade:number, emissao:'canhao'|'topo'|'estouro', lado?:'esquerda'|'direita'}>}
 */
export const roteiroDaCelebracao = (tipo) => {
  if (tipo === 'vendedor') {
    return [
      { aos: 0, quantidade: 54, emissao: 'estouro' },
      { aos: 700, quantidade: 32, emissao: 'estouro' },
      { aos: 1600, quantidade: 40, emissao: 'topo' },
    ];
  }

  const base = [
    { aos: 0, quantidade: 90, emissao: 'canhao', lado: 'esquerda' },
    { aos: 120, quantidade: 90, emissao: 'canhao', lado: 'direita' },
    { aos: 900, quantidade: 120, emissao: 'topo' },
    { aos: 2200, quantidade: 80, emissao: 'canhao', lado: 'esquerda' },
    { aos: 2350, quantidade: 80, emissao: 'canhao', lado: 'direita' },
    { aos: 3800, quantidade: 120, emissao: 'topo' },
    { aos: 5600, quantidade: 110, emissao: 'topo' },
  ];

  if (tipo !== 'supermeta') return base;

  return [
    ...base,
    { aos: 7200, quantidade: 110, emissao: 'canhao', lado: 'esquerda' },
    { aos: 7350, quantidade: 110, emissao: 'canhao', lado: 'direita' },
    { aos: 9000, quantidade: 140, emissao: 'topo' },
    { aos: 11000, quantidade: 140, emissao: 'topo' },
    { aos: 13200, quantidade: 120, emissao: 'topo' },
  ];
};

/** Quantidade total de tiras de papel de uma celebração, somando as ondas. */
export const papelDaCelebracao = (tipo) => roteiroDaCelebracao(tipo).reduce((total, onda) => total + onda.quantidade, 0);

/**
 * As cores saem do tema, não do código: `--accent` muda por empresa e o confete acompanha.
 * `getComputedStyle` devolve string vazia fora do navegador, e aí fica só o ouro do pódio.
 */
export const coresDoConfete = (raiz) => {
  const accent = raiz ? String(raiz.getPropertyValue('--accent') || '').trim() : '';
  return ['#fbbf24', '#f59e0b', '#ffffff', accent || '#a855f7'];
};

/**
 * Tiras de papel prontas para cair.
 *
 * `total` nasce na largura inteira, acima da borda: é chuva sobre o painel todo. `vendedor` nasce
 * num ponto e explode dali — a celebração menor precisa apontar para quem bateu a meta.
 *
 * `aleatorio` é injetável para o teste poder afirmar posição e quantidade.
 */
export const criarParticulas = ({ largura, altura, quantidade, origem = null, cores, emissao = null, aleatorio = Math.random }) => {
  const paleta = cores && cores.length ? cores : ['#fbbf24'];
  const modo = emissao || (origem ? 'estouro' : 'topo');

  return Array.from({ length: Math.max(0, Math.round(quantidade)) }, () => {
    // Uma em cada cinco é serpentina: tira longa e estreita, que cai torcendo. A variação de forma
    // é o que impede a cena de parecer um só objeto repetido.
    const serpentina = aleatorio() < 0.2;
    const comum = {
      giro: aleatorio() * Math.PI,
      velocidadeGiro: (aleatorio() - 0.5) * 14,
      largura: serpentina ? 3 + aleatorio() * 2 : 5 + aleatorio() * 6,
      altura: serpentina ? 18 + aleatorio() * 12 : 9 + aleatorio() * 10,
      cor: paleta[Math.floor(aleatorio() * paleta.length) % paleta.length],
      balanco: aleatorio() * Math.PI * 2,
    };

    if (modo === 'canhao') {
      // Sobe contra a gravidade e abre para o centro: é o movimento que se vê de longe, e por isso
      // ele abre a cena. O ângulo varre de 25° a 75° acima da horizontal.
      const daEsquerda = (origem?.x ?? 0) < largura / 2;
      const angulo = (25 + aleatorio() * 50) * (Math.PI / 180);
      const forca = 900 + aleatorio() * 700;
      return {
        ...comum,
        x: origem?.x ?? 0,
        y: origem?.y ?? altura,
        vx: Math.cos(angulo) * forca * (daEsquerda ? 1 : -1),
        vy: -Math.sin(angulo) * forca,
      };
    }

    if (modo === 'estouro') {
      const angulo = aleatorio() * Math.PI * 2;
      const forca = 420 + aleatorio() * 520;
      return {
        ...comum,
        x: origem?.x ?? largura / 2,
        y: origem?.y ?? altura / 2,
        vx: Math.cos(angulo) * forca,
        vy: Math.sin(angulo) * forca - 260,
      };
    }

    return {
      ...comum,
      x: aleatorio() * largura,
      y: -aleatorio() * altura * 0.4 - 20,
      vx: (aleatorio() - 0.5) * 120,
      vy: 120 + aleatorio() * 260,
    };
  });
};

/**
 * Um passo da queda. Devolve só quem ainda está em cena — é este descarte que encerra o laço.
 *
 * O balanço horizontal é o que separa papel de pedra: sem ele as tiras caem em linha reta e o
 * efeito lê como chuva de pixels.
 */
export const avancarParticulas = (particulas, dt, altura) => {
  const restantes = [];
  for (const p of particulas) {
    p.balanco += dt * 6;
    p.vy = p.vy + GRAVIDADE * dt;
    p.vx = p.vx * (1 - (1 - ARRASTO) * dt * 6) + Math.sin(p.balanco) * 26 * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.giro += p.velocidadeGiro * dt;
    if (p.y < altura + 60) restantes.push(p);
  }
  return restantes;
};

/** Nomes em texto de gente: "Ana", "Ana e Bia", "Ana, Bia e mais 2". */
export const textoDosNomes = (nomes = []) => {
  const lista = nomes.filter(Boolean);
  if (lista.length === 0) return '';
  if (lista.length === 1) return lista[0];
  if (lista.length === 2) return `${lista[0]} e ${lista[1]}`;
  return `${lista.slice(0, 2).join(', ')} e mais ${lista.length - 2}`;
};

export const anuncioDaCelebracao = (evento) => {
  if (!evento) return { titulo: '', detalhe: '' };
  if (evento.tipo === 'supermeta') {
    return {
      titulo: 'SUPERMETA BATIDA',
      detalhe: `${Number(evento.vendas || 0).toLocaleString('pt-BR')} vendas · supermeta de ${Number(evento.meta || 0).toLocaleString('pt-BR')}`,
    };
  }
  if (evento.tipo === 'total') {
    return {
      titulo: 'Meta do período batida',
      detalhe: `${Number(evento.vendas || 0).toLocaleString('pt-BR')} vendas · meta de ${Number(evento.meta || 0).toLocaleString('pt-BR')}`,
    };
  }
  const nomes = textoDosNomes(evento.nomes);
  return {
    titulo: evento.nomes?.length > 1 ? `${nomes} bateram a meta` : `${nomes} bateu a meta`,
    detalhe: 'Meta individual da competência atingida',
  };
};

const CelebracaoDeMeta = ({ eventos = [], onEncerrar }) => {
  const canvasRef = useRef(null);
  const particulasRef = useRef([]);
  const quadroRef = useRef(null);
  const anteriorRef = useRef(0);
  const processadosRef = useRef(new Set());
  // Os temporizadores vivem num ref, e não no objeto do evento: um cleanup por mudança de lista
  // cancelaria o encerramento do evento anterior a cada nova celebração, e o anúncio ficaria preso.
  const temporizadoresRef = useRef(new Map());
  const evento = eventos[eventos.length - 1] || null;

  const semMovimento = useMemo(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const desenhar = useCallback(() => {
    const canvas = canvasRef.current;
    const contexto = canvas?.getContext?.('2d');
    if (!canvas || !contexto) return;

    const agora = performance.now();
    const dt = Math.min((agora - anteriorRef.current) / 1000, 0.05);
    anteriorRef.current = agora;

    const { width, height } = canvas;
    const escala = window.devicePixelRatio || 1;
    contexto.clearRect(0, 0, width, height);
    particulasRef.current = avancarParticulas(particulasRef.current, dt, height / escala);

    for (const p of particulasRef.current) {
      contexto.save();
      contexto.translate(p.x * escala, p.y * escala);
      contexto.rotate(p.giro);
      contexto.fillStyle = p.cor;
      // A tira some e reaparece ao girar de perfil, como papel de verdade.
      contexto.globalAlpha = 0.35 + Math.abs(Math.cos(p.giro)) * 0.65;
      contexto.fillRect((-p.largura / 2) * escala, (-p.altura / 2) * escala, p.largura * escala, p.altura * escala);
      contexto.restore();
    }

    quadroRef.current = particulasRef.current.length ? requestAnimationFrame(desenhar) : null;
  }, []);

  const ajustarCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const escala = window.devicePixelRatio || 1;
    const largura = canvas.parentElement?.clientWidth || canvas.clientWidth || 0;
    const altura = canvas.parentElement?.clientHeight || canvas.clientHeight || 0;
    canvas.width = Math.round(largura * escala);
    canvas.height = Math.round(altura * escala);
    return { largura, altura };
  }, []);

  useEffect(() => {
    for (const item of eventos) {
      if (processadosRef.current.has(item.id)) continue;
      processadosRef.current.add(item.id);

      // O tempo do anúncio corre mesmo sem papel: reduced motion e aba escondida ainda precisam
      // devolver a tela ao estado normal.
      temporizadoresRef.current.set(item.id, setTimeout(() => {
        temporizadoresRef.current.delete(item.id);
        onEncerrar?.(item.id);
      }, duracaoDaCelebracao(item.tipo)));

      if (semMovimento || (typeof document !== 'undefined' && document.hidden)) continue;

      // Cada onda do roteiro entra na sua hora. Papel novo caindo sobre o que ainda não pousou é o
      // que sustenta a cena: uma rajada só termina em dois segundos, por mais papel que tenha.
      roteiroDaCelebracao(item.tipo).forEach((onda, indice) => {
        const chave = `${item.id}:onda-${indice}`;
        temporizadoresRef.current.set(chave, setTimeout(() => {
          temporizadoresRef.current.delete(chave);
          if (document.hidden) return;

          const medidas = ajustarCanvas();
          if (!medidas || !medidas.largura) return;

          const cores = coresDoConfete(window.getComputedStyle(document.documentElement));
          const origem = onda.emissao === 'canhao'
            ? { x: onda.lado === 'direita' ? medidas.largura : 0, y: medidas.altura }
            : onda.emissao === 'estouro'
              ? { x: medidas.largura / 2, y: medidas.altura * 0.34 }
              : null;

          particulasRef.current = [
            ...particulasRef.current,
            ...criarParticulas({ largura: medidas.largura, altura: medidas.altura, quantidade: onda.quantidade, cores, emissao: onda.emissao, origem }),
          ].slice(-TETO_DE_PARTICULAS);

          if (!quadroRef.current) {
            anteriorRef.current = performance.now();
            quadroRef.current = requestAnimationFrame(desenhar);
          }
        }, onda.aos));
      });
    }
  }, [eventos, onEncerrar, semMovimento, ajustarCanvas, desenhar]);

  // Papel parado numa aba oculta é trabalho jogado fora: o laço morre junto com a visibilidade.
  useEffect(() => {
    const aoEsconder = () => {
      if (!document.hidden) return;
      particulasRef.current = [];
      if (quadroRef.current) { cancelAnimationFrame(quadroRef.current); quadroRef.current = null; }
    };
    document.addEventListener('visibilitychange', aoEsconder);
    return () => document.removeEventListener('visibilitychange', aoEsconder);
  }, []);

  // Trocar para tela cheia no meio da celebração muda o tamanho do painel; sem reajustar, o papel
  // cairia esticado até o fim do efeito.
  useEffect(() => {
    const aoRedimensionar = () => { if (particulasRef.current.length) ajustarCanvas(); };
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, [ajustarCanvas]);

  useEffect(() => {
    const temporizadores = temporizadoresRef.current;
    return () => {
      if (quadroRef.current) cancelAnimationFrame(quadroRef.current);
      for (const temporizador of temporizadores.values()) clearTimeout(temporizador);
      temporizadores.clear();
    };
  }, []);

  const { titulo, detalhe } = anuncioDaCelebracao(evento);

  return (
    <div className="pointer-events-none absolute top-0 right-0 bottom-0 left-0 z-30 overflow-hidden" aria-hidden={evento ? undefined : 'true'}>
      <canvas ref={canvasRef} className="absolute top-0 right-0 bottom-0 left-0 h-full w-full" aria-hidden="true" />
      {/*
        O anúncio é o que o leitor de tela recebe e o que sobrevive ao `prefers-reduced-motion`:
        sem ele, quem não vê o confete não fica sabendo de nada.
      */}
      {/* Clarão dourado no primeiro instante: é ele que puxa o olho de quem está de costas, antes
          mesmo de o papel subir. Dura pouco e não volta — pulsar sem parar cansaria em dez minutos. */}
      {evento && !semMovimento && (
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 bottom-0 left-0 bg-[radial-gradient(circle_at_50%_38%,rgba(251,191,36,0.34),transparent_62%)]"
          style={{ animation: 'celebracao-clarao 1500ms cubic-bezier(0.16, 1, 0.3, 1) both' }}
        />
      )}
      <div role="status" aria-live="polite" className="absolute inset-x-0 top-16 flex justify-center px-4 sm:top-10">
        {evento && (
          <div
            className={`flex max-w-[min(94vw,860px)] items-center gap-4 rounded-3xl border bg-slate-950/88 px-6 py-5 text-left shadow-[0_32px_90px_rgba(2,6,23,0.65)] backdrop-blur-xl sm:gap-5 sm:px-8 sm:py-6 ${evento.tipo === 'supermeta' ? 'border-amber-300' : 'border-amber-300/60'}`}
            style={{ animation: 'celebracao-entrada 620ms cubic-bezier(0.16, 1, 0.3, 1) both' }}
          >
            <span
              className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-amber-300 text-slate-950 shadow-[0_10px_30px_rgba(251,191,36,0.45)] sm:h-20 sm:w-20"
              style={semMovimento ? undefined : { animation: 'celebracao-trofeu 1800ms ease-in-out 620ms infinite' }}
            >
              <Trophy className="h-8 w-8 sm:h-10 sm:w-10" />
            </span>
            <span className="min-w-0">
              <strong className="block text-2xl font-black leading-tight tracking-tight text-amber-200 sm:text-4xl">{titulo}</strong>
              <span className="mt-1 block truncate text-sm font-medium text-white/75 sm:text-lg">{detalhe}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CelebracaoDeMeta;
